import { Location, APP_BASE_HREF } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { of, Subject } from 'rxjs';

import { BookingOptionDoc } from '@fry/booking/lib/booking-items/booking-option';
import { BookingOptionsStore } from '@fry/booking/lib/booking-items/booking-options.store';
import { ListDefaults, TableService } from '@fry/lib/list';
import { EasTable } from '@fry/lib/store';
import { OptionBookingsSearch } from './option-bookings-search';
import { BookingsStore } from '@fry/booking/lib/bookings';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BookingItemOptionApplyComponent } from './booking-item-option-apply.component';
import { EASButtonAction } from '@fry/components/common/buttons/buttons.service';
import { SecurityService } from '@fry/lib/security';
import { FieldType, FormField } from '@fry/lib/forms';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { SelectedItems } from '@fry/components/common/list/interactive-table/interactive-table.interface';
import { AsyncTasksWizardStore } from '@fry/async-tasks/lib';
import { InteractiveTableOptions } from '@fry/components/common/list/interactive-table/interactive-table.component';
import { NotifyService } from '@fry/lib/utils';
import { AuthService } from '@fry/lib/auth';

@Component({
    selector: 'eas-booking-item-option-view',
    templateUrl: './booking-item-option-view.component.html',
    styleUrls: ['./booking-item-option-view.component.css'],
    providers: [
        OptionBookingsSearch,
        TableService,
    ],
    standalone: false
})
export class BookingItemOptionViewComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();

  public bookingItemOption?: BookingOptionDoc;
  public tableComponentOptions: InteractiveTableOptions = {
    enableColumns: true,
    enableFilters: true,
    enableExport: true,
    enableSelection: false,
    itemSingleLabel: $localize `booking`,
    itemPluralLabel: $localize `bookings`,
  };

  public tableLoaded = false;

  // public bookingItemOption$: Observable<BookingOptionDoc>;
  public loaded$ = new Subject<boolean>();
  private loading = false;

  public user = new UntypedFormControl();

  public applyUrl: string | undefined;
  public $applyUrlVisible = signal<boolean>(false);

  public optionTable: EasTable | undefined;

  public buttons: EASButtonAction[] = [
    {
      type: 'button',
      label: $localize `Force apply`,
      color: 'primary',
      condition: () => this.security.hasPermission('bookings.manage'),
      click: () => {
        this.forceApply();
      }
    },
    {
      type: 'button',
      label: $localize `Show apply URL`,
      color: 'primary',
      condition: () => {
        return of(this.bookingItemOption?.bookableType === 'bookable');
      },
      click: () => {
        this.$applyUrlVisible.update(value => !value);
      }
    },
    {
      type: 'button',
      label: $localize `Back`,
      color: 'warn',
      condition: () => of(true),
      click: () => {
        this.goBack();
      }
    }
  ];

  public selectedItems: SelectedItems = { total: 0 };

  constructor(
    private bookingsStore: BookingsStore,
    private bookingOptionsStore: BookingOptionsStore,
    private asyncTasksWizard: AsyncTasksWizardStore,
    private tableService: TableService,
    private location: Location,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private security: SecurityService,
    private notify: NotifyService,
    private auth: AuthService,
    @Inject(APP_BASE_HREF) private baseHref: string,
  ) {
    this.loaded$.next(false);
  }

  ngOnInit() {
    this.route.paramMap.subscribe(paramMap => {
      this.loaded$.next(false);
      this.bookingItemOption = undefined;
      const bookingItemID = paramMap.get('id');
      const bookingOptionID = paramMap.get('boid') || 'root';
      if (bookingOptionID && bookingItemID) {
        this.loadBookingOption(bookingItemID, bookingOptionID).pipe(
          takeUntil(this.destroyed$),
          switchMap(() => {
            if (this.bookingItemOption === undefined) {
              throw new Error($localize `Booking option not initialised`);
            }

            const org = this.auth.current.organisation.replace('org_', '');
            this.applyUrl = window.location.origin + this.router.createUrlTree([this.baseHref, 'org', org, 'bookings', 'apply', this.bookingItemOption.bookingItem._id, this.bookingItemOption.id]).toString();

            return this.loadBookings(
              this.bookingItemOption.bookingItem._id,
              this.bookingItemOption.id
            );
          }),
          takeUntil(this.destroyed$),
        ).subscribe();
      }
    });
  }

  loadBookings(bookingItemID: string, bookingOptionID: string) {
    // EAS Table accepts async list.table$
    const defaults: ListDefaults = { listType: 'table', engine: 'es', pageSize: 10 };
    defaults.fixedSearchModel = {
      filter: {
        bookingItem: bookingItemID,
        bookingOptionPath: bookingOptionID,
      },
      sort: {sort_on: 'createdDate', sort_order: 'desc'},
      select: ["user", "stateName", "createdDate", "modifiedDate", "view"],
      options: {
        includeFormFields: true
      },
    };
    defaults.hideFilters = ['bookingItem', 'bookingOptionUnique'];
    defaults.exportFormFields = this.getExportFormFields();
    defaults.featuredFilters = ['text', 'es_extra.stateName'];
    defaults.preserveState = true;
    return this.security.hasPermission('bookings.manage').pipe(
      switchMap(has => {
        this.tableComponentOptions.enableSelection = has;
        this.tableLoaded = true;

        return this.tableService
          .setupWith(this.bookingsStore,
                    defaults,
                    `booking-option-view-${bookingItemID}`
          );
      }))
  }

  loadBookingOption(bookingItemID: string, bookingOptionID: string) {
    this.bookingItemOption = undefined;
    return this.bookingOptionsStore
      .get(bookingOptionID, bookingItemID).pipe(
        tap(option => {
          this.bookingItemOption = option.orig as BookingOptionDoc;
          this.tableComponentOptions.tableTitle = $localize `Bookings for ${this.bookingItemOption.title}`;
          this.loaded$.next(true);
        })
      );
  }

  public goBack() {
    this.location.back();
  }

  public doReload() {
    this.loading = true;
    this.tableService.reload().subscribe({
      next: () => {
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  public onSelectedItems(value: SelectedItems) {
    this.selectedItems = value;
  }

  doBulkAction() {
    if (this.selectedItems.total === 0) {
      throw new Error("Cannot bulk if empty");
    }

    var title = this.bookingItemOption?.bookingItem.name;
    if (title !== this.bookingItemOption?.title) {
      title = title?.concat(` - ${this.bookingItemOption?.title}`);
    }
    const data = {
      itemDocType: "booking",
      data: {
        source: {
          ids: this.selectedItems.ids,
          searchCriteria: this.selectedItems.searchCriteria?.filter
        },
        params: {},
        fixedSearch: this.tableService.getFixedModel(),
        search: this.tableService.getSearchModel(),
      },
      origin: {
        path: this.router.url,
        title: title,
      }
    }
    const task = this.asyncTasksWizard.blank(data);
    this.asyncTasksWizard.create(task).subscribe(data => {
      this.router.navigate([`bulk-actions/${data.id}/wizard`]);
    });
  }

  public forceApply() {
    if (this.bookingItemOption === undefined) {
      throw new Error($localize `Cannot force apply if no option is loaded`);
    }
    this.dialog.open(BookingItemOptionApplyComponent, {
      data: {
        bookingOption: this.bookingItemOption.id,
        bookingItem: this.bookingItemOption.bookingItem._id,
        bookableType: this.bookingItemOption.bookableType
      }
    }).afterClosed().subscribe((result?: { id: string }) => {
      if (result) {
        this.router.navigate(['bookings', result.id, 'edit'])
      }
    });
  }

  public isLoading() {
    return this.loading;
  }

  private getExportFormFields(): FormField[] {
    return [
      {
        id: 'includeUserFields',
        type: FieldType.FIELD,
        title: $localize `Include profile fields`,
        initial: true,
        widget: {
          type: 'boolean'
        }
      },
      {
        id: 'includeFormFields',
        type: FieldType.FIELD,
        title: $localize `Include form data`,
        widget: {
          type: 'boolean'
        }
      }
    ];
  }

  public copyApplyUrl() {
    if (this.applyUrl) {
      navigator.clipboard.writeText(this.applyUrl).then(() => {
        this.notify.success($localize `URL copied to clipboard`);
      });
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

}
